<script lang="ts" setup>
import { computed, inject, onBeforeUnmount, onMounted, provide, ref, shallowRef, watch } from 'vue'
import ServiceEditor from './ServiceEditor.vue'
import FormAlert from '@/components/FormAlert.vue'
import BbelPalette from './bbel/BbelPalette.vue'
import {
  BBEL_PICKER_HOST_KEY,
  BBEL_SCOPE_KEY,
  EMPTY_BBEL_SCOPE,
  type BbelInsertTarget,
  type BbelPickerHost,
  type BbelScopeContext,
} from '../composables/bbel-context'
import { serviceTypeDisplay, type Service } from '@/types'

const props = defineProps<{
  modelValue: Service | null
  isVisible: boolean
  isDirty?: boolean
  /** Save-mutation error, rendered in the panel's FormAlert (same pattern as the publish modal). */
  saveError?: unknown
}>()

const emit = defineEmits<{
  'update:modelValue': [service: Service]
  close: []
  run: [serviceId: string, serviceData: Service]
  save: []
  discard: []
  'toggle-component': [serviceId: string, isComponent: boolean]
  'dismiss-error': []
}>()

const statusBadgeClass = computed(() => {
  if (!props.modelValue) return 'bg-secondary'
  return 'bg-secondary'
})

const serviceTypeInfo = computed(() => serviceTypeDisplay(props.modelValue?.serviceModelType?.$type))

// --- BBEL smart picker column (#5683). Two modes, decided purely by viewport width: where panel +
// picker fit side by side, the picker is a PERMANENT column (layout fixed for the panel's whole
// lifetime — nothing ever shifts on focus); where they don't, it reverts to on-demand — opening on
// field focus and OVERLAYING the panel's right edge (hover, never push). Fields register as the
// insert target on focus (BBEL_PICKER_HOST_KEY); inserts route to the last-focused one. ---
const bbelScope = inject<BbelScopeContext>(BBEL_SCOPE_KEY, computed(() => EMPTY_BBEL_SCOPE))
// shallowRef: the target carries its own caretPaths Ref — deep ref() would unwrap it away.
const activeBbelTarget = shallowRef<BbelInsertTarget | null>(null)

const dockQuery = window.matchMedia('(min-width: 1320px)')
const canDock = ref(dockQuery.matches)
const overlayOpen = ref(false)
const pickerVisible = computed(() => canDock.value || overlayOpen.value)

// Mount-order registry so a cold-opened picker can auto-target the first field. Field focus only
// moves the target — it never summons the overlay (the rail is the explicit narrow-screen trigger;
// autocomplete serves the typing flow).
const registeredTargets: BbelInsertTarget[] = []
provide<BbelPickerHost>(BBEL_PICKER_HOST_KEY, {
  active: activeBbelTarget,
  setActive: (target) => {
    if (activeBbelTarget.value !== target) activeBbelTarget.value = target
  },
  register: (target) => {
    registeredTargets.push(target)
    return () => {
      const i = registeredTargets.indexOf(target)
      if (i >= 0) registeredTargets.splice(i, 1)
      if (activeBbelTarget.value === target) activeBbelTarget.value = null
    }
  },
})

// Cold open (rail click before any field was focused): auto-target the first field — insert()
// focuses it, so nothing lands invisibly.
function openOverlay() {
  activeBbelTarget.value ??= registeredTargets[0] ?? null
  overlayOpen.value = true
}

const pickerCaretPaths = computed(() => activeBbelTarget.value?.caretPaths.value ?? [])

function handlePickerInsert(bare: string, cursorBackBy: number) {
  activeBbelTarget.value?.insert(bare, cursorBackBy)
}

// --- Overlay session (on-demand mode only): interaction inside the picker or any BBEL field keeps
// it open; anywhere else (click or Tab) closes it, as does Esc. composedPath() so CodeMirror's
// synchronous DOM rebuilds can't fake an "outside". ---
const pickerWrapEl = ref<HTMLElement | null>(null)

const isPickerInteraction = (e: Event) =>
  e.composedPath().some(
    (t) =>
      t instanceof HTMLElement &&
      (t === pickerWrapEl.value || t.classList.contains('bbel-field-wrap')),
  )

function onDocPointerDown(e: MouseEvent) {
  if (!isPickerInteraction(e)) overlayOpen.value = false
}
function onDocFocusIn(e: FocusEvent) {
  if (!isPickerInteraction(e)) overlayOpen.value = false
}
function onOverlayKey(e: KeyboardEvent) {
  if (e.key === 'Escape') overlayOpen.value = false
}

watch(overlayOpen, (open) => {
  if (open) {
    document.addEventListener('mousedown', onDocPointerDown)
    document.addEventListener('focusin', onDocFocusIn)
    document.addEventListener('keydown', onOverlayKey)
  } else {
    document.removeEventListener('mousedown', onDocPointerDown)
    document.removeEventListener('focusin', onDocFocusIn)
    document.removeEventListener('keydown', onOverlayKey)
  }
})

const onDockChange = (e: MediaQueryListEvent) => {
  canDock.value = e.matches
  if (e.matches) overlayOpen.value = false
}
onMounted(() => dockQuery.addEventListener('change', onDockChange))
onBeforeUnmount(() => {
  dockQuery.removeEventListener('change', onDockChange)
  overlayOpen.value = false // detaches the document listeners via the watcher
})

function handleClose() {
  emit('close')
}
</script>

<template>
  <!-- Teleport escapes the builder canvas's pan/zoom transforms — a transformed ancestor hijacks
       position: fixed, so the modal would size and centre against the canvas, not the viewport
       (same reason BbelEditorModal teleports). -->
  <Teleport to="body">
  <div
    v-if="isVisible"
    class="modal d-block"
    role="dialog"
    aria-modal="true"
    @mousedown.self="handleClose"
  >
    <div class="modal-dialog modal-dialog-centered modal-dialog-scrollable service-details-dialog">
      <!-- Shell: editor + the permanent BBEL picker column, height-matched as one unit. -->
      <div class="service-details-shell">
        <div class="modal-content">
          <div class="modal-header">
            <div class="d-flex align-items-center gap-3">
              <div
                class="service-icon bg-light-subtle border d-flex align-items-center justify-content-center"
              >
                <i class="service-detail-icon" :class="serviceTypeInfo.icon"></i>
              </div>
              <div>
                <h2 class="modal-title">{{ serviceTypeInfo.name }}</h2>
                <small class="text-muted">{{ modelValue?.id || '0' }}</small>
              </div>
            </div>
            <!-- ms-auto does the right-aligning: modal-header only right-aligns a bare .btn-close
                 (margin-left auto on the button itself), which nesting it here defeats. -->
            <div class="d-flex align-items-center gap-2 ms-auto">
              <span class="badge" :class="statusBadgeClass">
                {{ modelValue ? 'active' : 'N/A' }}
              </span>
              <button type="button" class="btn-close" @click="handleClose"></button>
            </div>
          </div>

          <div class="modal-body">
            <FormAlert :error="saveError" @dismiss="emit('dismiss-error')" />
            <ServiceEditor
              :model-value="modelValue"
              :is-dirty="isDirty"
              :show-footer="false"
              @update:model-value="(svc) => emit('update:modelValue', svc)"
              @save="emit('save')"
              @discard="emit('discard')"
              @run="(id, data) => emit('run', id, data)"
              @toggle-component="(id, v) => emit('toggle-component', id, v)"
            />
          </div>

          <div class="modal-footer justify-content-between">
            <div class="d-flex gap-2">
              <button
                type="button"
                class="bb-btn-secondary-cta btn-sm"
                @click="modelValue && emit('run', modelValue.id, modelValue)"
              >
                <i class="bi bi-play me-1"></i>
                <span class="d-none d-sm-inline">Run Service</span>
                <span class="d-inline d-sm-none">Run</span>
              </button>
              <button type="button" class="bb-btn-secondary-cta btn-sm">
                <i class="bi bi-code me-1"></i>
                <span class="d-none d-sm-inline">View Code</span>
                <span class="d-inline d-sm-none">Code</span>
              </button>
            </div>
            <div class="d-flex gap-2">
              <button
                type="button"
                class="bb-btn-primary-cta btn-sm"
                :disabled="!isDirty"
                @click="emit('save')"
              >
                <i class="bi bi-save me-1"></i>
                Save
              </button>
              <button type="button" class="bb-btn-cancel" @click="handleClose">
                Close
              </button>
            </div>
          </div>
        </div>

        <!-- Narrow screens: a full-height rail on the panel's edge summons the picker overlay. -->
        <button
          v-if="!canDock && !overlayOpen"
          type="button"
          class="bbel-picker-rail"
          title="Insert data or a function"
          aria-label="Open the insert panel"
          @click="openOverlay"
          @mousedown.prevent
        >
          <i class="bi bi-chevron-left"></i>
        </button>

        <!-- Picker: permanent column when it fits, rail-summoned overlay over the right edge when
             it doesn't. Full panel height either way. -->
        <div
          v-if="pickerVisible"
          ref="pickerWrapEl"
          class="service-details-picker-wrap"
          :class="canDock ? 'picker-docked' : 'picker-overlay'"
        >
          <BbelPalette
            class="service-details-picker shadow-lg"
            :scope="bbelScope"
            :active-paths="pickerCaretPaths"
            :can-insert="!!activeBbelTarget"
            :closable="!canDock"
            @insert="handlePickerInsert"
            @close="overlayOpen = false"
          />
        </div>
      </div>
    </div>
  </div>
  <div v-if="isVisible" class="modal-backdrop fade show"></div>
  </Teleport>
</template>

<style lang="scss" scoped>
/* Wider than Bootstrap's largest stock dialog — room for the two-column form + picker column.
   Everything else (centring, visible-viewport height cap, pinned header/footer with a scrolling
   body) comes stock from modal-dialog-centered + modal-dialog-scrollable. */
.service-details-dialog {
  max-width: min(1450px, 95vw);
  width: 95%;
}

/* Editor + picker, height-matched as one unit: the editor drives the height (capped by the
   scrollable dialog), the picker column stretches to follow it. Docked mode fixes the layout for
   the panel's whole lifetime — nothing shifts when fields gain or lose focus; overlay mode floats
   the picker over the editor's right edge instead of pushing anything. */
.service-details-shell {
  position: relative;
  display: flex;
  align-items: stretch;
  gap: 0.5rem;
  width: 100%;
  max-height: 100%;
  /* .modal-dialog turns pointer events off (backdrop clicks); .modal-content turns its own back
     on, but the rail and the picker column sit beside it and need theirs restored here. */
  pointer-events: auto;
}

.service-details-shell .modal-content {
  /* wide enough for two-column service forms; flexes down before the picker column does */
  flex: 1 1 1100px;
  min-width: 0;
  max-width: 1100px;
}

/* Docked: the picker follows the editor card's height instead of contributing its own — the
   palette is absolutely sized against this wrap, so a long function list scrolls inside the
   palette rather than stretching the whole shell (and tab switches never change the height).
   Width must match .bbel-palette's own (340px). */
.service-details-picker-wrap.picker-docked {
  position: relative;
  width: 340px;
  flex-shrink: 0;
}
.service-details-picker-wrap.picker-docked .service-details-picker {
  position: absolute;
  inset: 0;
}

.service-details-picker-wrap.picker-overlay {
  position: absolute;
  top: 0;
  right: 0;
  height: 100%;
  z-index: 10;
}

.service-details-picker {
  height: 100%;
}

/* Narrow-screen summon rail: full height, slim, glued to the panel's right edge — a big touch
   target that also marks where the picker will appear. */
.bbel-picker-rail {
  flex-shrink: 0;
  width: 1.25rem;
  align-self: stretch;
  display: grid;
  place-items: center;
  border: 1px solid var(--bs-border-color);
  border-radius: 0.375rem;
  background: var(--bs-body-bg);
  color: var(--bs-secondary-color);
  padding: 0;
  font-size: 0.7rem;
}
.bbel-picker-rail:hover {
  color: var(--bs-primary);
  background: var(--bs-tertiary-bg);
}

.service-icon {
  width: 40px;
  height: 40px;
  border-radius: 8px;
  font-size: 18px;
  flex-shrink: 0;
}

.service-detail-icon {
  color: var(--bs-secondary-color);
}
</style>
